import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { getExamIncidents, createExamIncident } from "../../../services/ExamIncidentService";
import { getExamSessions } from "../../../services/ExamSessionService";
import { getExams } from "../../../services/ExamService";
import { getRooms } from "../../../services/RoomService";
import { getStudentProfiles } from "../../../services/StudentProfileService";
import { getUsers } from "../../../services/UserService";
import { useTranslation } from "react-i18next";
import { getLocale, lazyLabels } from "../../../utils/i18nLabels";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";
import { pdf } from "@react-pdf/renderer";
import IncidentPvPDF from "./IncidentPvPDF";

const typeLabels = lazyLabels({ fraud: "pages.examIncidents.fraude", technical_interruption: "pages.examIncidents.interruptionTechnique", other: "pages.examIncidents.autre" });
const typeBadge = { fraud: "danger", technical_interruption: "warning", other: "secondary" };

const emptyForm = {
  exam_session_id: "",
  room_id: "",
  student_id: "",
  type: "other",
  description: "",
  penalty_minutes: "",
};

const ExamIncidents = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.EXAM_INCIDENT_CREATE);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [sessions, setSessions] = useState([]);
  const [exams, setExams] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [sessionFilter, setSessionFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownloadPv = async (incident) => {
    setDownloadingId(incident.id);
    try {
      const blob = await pdf(
        <IncidentPvPDF
          incident={incident}
          context={{
            sessionLabel: sessionLabel(incident.exam_session_id),
            roomLabel: roomLabel(incident.room_id),
            studentLabel: studentLabel(incident.student_id),
            reporterLabel: reporterLabel(incident.reported_by),
          }}
        />
      ).toBlob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `pv-incident-${incident.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.examIncidents.impossibleGenererPvPourIncident") });
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    getExamSessions({ page: 1, limit: 200 }).then((res) => setSessions(res.data?.data || [])).catch(() => {});
    getExams({ page: 1, limit: 200 }).then((res) => setExams(res.data?.data || [])).catch(() => {});
    getRooms({ page: 1, limit: 200 }).then((res) => setRooms(res.data?.data || [])).catch(() => {});
    getStudentProfiles({ page: 1, limit: 200 }).then((res) => setStudents(res.data?.data || [])).catch(() => {});
    getUsers({ page: 1, limit: 200 }).then((res) => setUsers(res.data?.data || [])).catch(() => {});
  }, []);

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "created_at", sort_dir: "desc" };
      if (sessionFilter) params.exam_session_id = sessionFilter;
      if (typeFilter) params.type = typeFilter;

      const res = await getExamIncidents(params);
      const body = res.data;
      setIncidents(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.examIncidents.impossibleChargerIncidents"));
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, sessionFilter, typeFilter, t]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const roomLabel = (id) => rooms.find((r) => r.id === id)?.name || "-";
  const studentLabel = (id) => {
    const profile = students.find((s) => s.id === id);
    if (!profile) return "-";
    const user = users.find((u) => u.id === profile.user_id);
    return user ? `${user.email} (${profile.matricule})` : profile.matricule;
  };
  const sessionLabel = (id) => {
    const session = sessions.find((s) => s.id === id);
    if (!session) return "-";
    const exam = exams.find((e) => e.id === session.exam_id);
    const date = session.start_at ? new Date(session.start_at).toLocaleString(getLocale()) : "";
    return `${exam?.title || t("common.ui.examen")}${date ? ` — ${date}` : ""}`;
  };
  const reporterLabel = (id) => {
    const user = users.find((u) => u.id === id);
    if (!user) return "-";
    return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email;
  };

  const resetFilters = () => {
    setSessionFilter("");
    setTypeFilter("");
    setPage(1);
  };

  const openAddModal = () => {
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.exam_session_id) errors.exam_session_id = t("pages.examIncidents.laSessionExamenEstRequise");
    if (!formData.type) errors.type = t("pages.examIncidents.leTypeIncidentEstRequis");
    if (!formData.description.trim() || formData.description.trim().length < 3) {
      errors.description = t("pages.examIncidents.laDescriptionEstRequise3Caracteres");
    }
    if (formData.type === "fraud" && !formData.student_id) {
      errors.student_id = t("pages.examIncidents.unIncidentTypeFraudeDoitEtre");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        exam_session_id: formData.exam_session_id,
        room_id: formData.room_id || null,
        student_id: formData.student_id || null,
        type: formData.type,
        description: formData.description.trim(),
        ...(formData.type === "fraud" && formData.penalty_minutes
          ? { penalty_minutes: Number(formData.penalty_minutes) }
          : {}),
      };
      await createExamIncident(payload);
      setFeedback({ type: "success", message: t("pages.examIncidents.incidentConsignePvNumeriqueEnregistre") });
      setShowFormModal(false);
      fetchIncidents();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("common.crud.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("pages.examIncidents.incidentsExamen")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" title={t("common.crud.refresh")} onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            {canCreate && (
              <div className="mb-2">
                <button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                  <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.examIncidents.consignerIncident")}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="alert alert-info">
          {t("pages.examIncidents.chaqueIncidentConsigneFraudeInterruptionTechnique")}
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.examIncidents.journalIncidents")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={sessionFilter}
                  onChange={(e) => {
                    setSessionFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{ minWidth: "220px" }}
                >
                  <option value="">{t("common.ui.toutesSessions")}</option>
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {sessionLabel(s.id)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("pages.examIncidents.tousTypes")}</option>
                  <option value="fraud">{t("pages.examIncidents.fraude")}</option>
                  <option value="technical_interruption">{t("pages.examIncidents.interruptionTechnique")}</option>
                  <option value="other">{t("pages.examIncidents.autre")}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="card-body p-0 py-3">
            <div className="table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.ui.date")}</th>
                    <th>{t("pages.examIncidents.session")}</th>
                    <th>{t("pages.examIncidents.salle")}</th>
                    <th>{t("roles.STUDENT")}</th>
                    <th>{t("common.fields.type")}</th>
                    <th>{t("common.fields.description")}</th>
                    <th>{t("pages.examIncidents.pv")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : incidents.length > 0 ? (
                    incidents.map((incident) => (
                      <tr key={incident.id}>
                        <td>{incident.created_at ? new Date(incident.created_at).toLocaleString(getLocale()) : "-"}</td>
                        <td>{sessionLabel(incident.exam_session_id)}</td>
                        <td>{incident.room_id ? roomLabel(incident.room_id) : "-"}</td>
                        <td>{incident.student_id ? studentLabel(incident.student_id) : "-"}</td>
                        <td>
                          <span className={`badge badge-soft-${typeBadge[incident.type] || "secondary"}`}>
                            {typeLabels[incident.type] || incident.type}
                          </span>
                          {incident.penalty_minutes ? (
                            <span className="badge badge-soft-dark ms-1" title={t("pages.examIncidents.penaliteTempsAppliquee")}>
                              {t("pages.examIncidents.min", { penalty_minutes: incident.penalty_minutes })}
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <span className="text-truncate d-inline-block" style={{ maxWidth: "320px" }} title={incident.description}>
                            {incident.description}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            disabled={downloadingId === incident.id}
                            onClick={() => handleDownloadPv(incident)}
                          >
                            <i className="ti ti-file-download me-1"></i>
                            {downloadingId === incident.id ? "..." : "PV"}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.examIncidents.aucunIncidentConsigne")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center px-3 pt-3 border-top">
                <span className="text-sm text-gray-600">{t("pages.examIncidents.incident", { total })}</span>
                <div className="d-flex gap-1">
                  <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                    {t("common.ui.precedent")}
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                    {t("common.ui.suivant")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered size="lg">
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{t("pages.examIncidents.consignerIncident")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            <div className="mb-3">
              <label className="form-label">
                {t("common.ui.sessionExamen")} <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${formErrors.exam_session_id ? "is-invalid" : ""}`}
                value={formData.exam_session_id}
                onChange={(e) => handleFormChange("exam_session_id", e.target.value)}
              >
                <option value="">{t("common.crud.selectPlaceholder")}</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {sessionLabel(s.id)}
                  </option>
                ))}
              </select>
              {formErrors.exam_session_id && <div className="invalid-feedback d-block">{formErrors.exam_session_id}</div>}
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.examIncidents.salleOptionnel")}</label>
                <select
                  className="form-select"
                  value={formData.room_id}
                  onChange={(e) => handleFormChange("room_id", e.target.value)}
                >
                  <option value="">{t("common.fields.noneFem")}</option>
                  {rooms.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("pages.examIncidents.typeIncident")} <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${formErrors.type ? "is-invalid" : ""}`}
                  value={formData.type}
                  onChange={(e) => handleFormChange("type", e.target.value)}
                >
                  <option value="fraud">{t("pages.examIncidents.fraude")}</option>
                  <option value="technical_interruption">{t("pages.examIncidents.interruptionTechnique")}</option>
                  <option value="other">{t("pages.examIncidents.autre")}</option>
                </select>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">
                {t("pages.examIncidents.etudiantConcerne")} {formData.type === "fraud" && <span className="text-danger">*</span>}
                {formData.type !== "fraud" && " (optionnel)"}
              </label>
              <select
                className={`form-select ${formErrors.student_id ? "is-invalid" : ""}`}
                value={formData.student_id}
                onChange={(e) => handleFormChange("student_id", e.target.value)}
              >
                <option value="">{t("pages.examIncidents.aucunIncidentCollectif")}</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>
                    {studentLabel(s.id)}
                  </option>
                ))}
              </select>
              {formErrors.student_id && <div className="invalid-feedback d-block">{formErrors.student_id}</div>}
            </div>
            {formData.type === "fraud" && (
              <div className="mb-3">
                <label className="form-label">{t("pages.examIncidents.penaliteTempsMinutesOptionnel")}</label>
                <input
                  type="number"
                  min="0"
                  max="600"
                  className="form-control"
                  value={formData.penalty_minutes}
                  onChange={(e) => handleFormChange("penalty_minutes", e.target.value)}
                  placeholder={t("pages.examIncidents.ex10")}
                />
                <small className="text-muted">
                  {t("pages.examIncidents.siEtudiantTentativeCoursSur")}
                </small>
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">
                {t("common.fields.description")} <span className="text-danger">*</span>
              </label>
              <textarea
                className={`form-control ${formErrors.description ? "is-invalid" : ""}`}
                rows={4}
                placeholder={t("pages.examIncidents.decrivezPrecisementFaitsConstates")}
                value={formData.description}
                onChange={(e) => handleFormChange("description", e.target.value)}
              ></textarea>
              {formErrors.description && <div className="invalid-feedback d-block">{formErrors.description}</div>}
            </div>
            <div className="alert alert-warning mb-0">
              {t("pages.examIncidents.uneFoisEnregistreProcesVerbalNe")}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : t("pages.examIncidents.consignerIncident2")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default ExamIncidents;
